var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import classNames from "classnames";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import Input from "@widget/forms/inputs/Input";
import PasswordInput from "@widget/forms/inputs/PasswordInput";
import Checkbox from "@widget/forms/inputs/Checkbox";
import FormField from "@widget/forms/inputs/FormField";
import Icon from "@icon/Icon";
import SSOButtons from "../components/SSOButtons";
import LanguageSelector from "../components/LanguageSelector";
import ForgotPassword from "./ForgotPassword";
import { loginUser } from "@core/services/auth_services";
import { broadcastAuthEvent } from '@core/auth/authBroadcast';
import { $L } from "../utils/authLocalization";
import useLoadingFeedback from "@notifications/LoadingFeedback/useLoadingFeedback";
const LoginForm = (props) => {
    const { accountUrl, nextUrl, accountDisplayName, ssoConfig, allowPasswordReset, managePasswords, disablePasswordMemory, captchaEnabled, captchaSiteKey, config, currentLanguage, availableLanguages, onLanguageChange } = props;
    const loadingFeedback = useLoadingFeedback();
    const [loginID, setLoginID] = useState("");
    const [password, setPassword] = useState("");
    const [keepLoggedIn, setKeepLoggedIn] = useState(false);
    const [error, setError] = useState("");
    const [loginIDError, setLoginIDError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [flashMessage, setFlashMessage] = useState("");
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    // Extract flash message from URL params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const message = params.get('message');
        if (message) {
            setFlashMessage(decodeURIComponent(message));
        }
    }, []);
    const handleLogin = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        // Reset error states
        setLoginIDError(false);
        setPasswordError(false);
        setError('');
        // Validate inputs
        if (!loginID || !password) {
            setError($L('uxp-core.auth.enter-user-id-password', currentLanguage));
            if (!loginID)
                setLoginIDError(true);
            if (!password)
                setPasswordError(true);
            return;
        }
        loadingFeedback.show({ message: $L('uxp-core.auth.logging-in', currentLanguage) });
        try {
            const result = yield loginUser({
                LoginID: loginID,
                LoginPassword: password,
                AuthType: 'User',
                Next: nextUrl || '/view/system/home',
                Remember: keepLoggedIn
            }, accountUrl);
            if (result.successful) {
                // Use NextUrl from backend (handles TOS/agreement redirects), fall back to nextUrl prop
                const redirectTo = ((_a = result.data) === null || _a === void 0 ? void 0 : _a.NextUrl) || nextUrl || '/view/system/home';
                broadcastAuthEvent({ type: 'login' });
                window.location.href = redirectTo;
            }
            else {
                setError($L('uxp-core.auth.login-failed', currentLanguage));
                setLoginIDError(true);
                setPasswordError(true);
                loadingFeedback.hide();
            }
        }
        catch (err) {
            setError($L('uxp-core.auth.login-failed', currentLanguage));
            setLoginIDError(true);
            setPasswordError(true);
            loadingFeedback.hide();
        }
    });
    // Handle loginID input change - clear errors when user types
    const handleLoginIDChange = (val) => {
        setLoginID(val);
        if (loginIDError)
            setLoginIDError(false);
        if (error)
            setError('');
    };
    // Handle password input change - clear errors when user types
    const handlePasswordChange = (val) => {
        setPassword(val);
        if (passwordError)
            setPasswordError(false);
        if (error)
            setError('');
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && loginID && password) {
            handleLogin();
        }
    };
    const handleForgotPassword = () => {
        setShowForgotPassword(true);
    };
    // Determine form type and settings from config
    const formType = (config === null || config === void 0 ? void 0 : config.loginForm.type) || 'island-vertical';
    const isIslandHorizontal = formType === 'island-horizontal';
    const isIslandVertical = formType === 'island-vertical';
    const isFullpage = formType === 'fullpage';
    // Get active island config
    const islandHorizontalCfg = config === null || config === void 0 ? void 0 : config.loginForm.islandHorizontal;
    const islandVerticalCfg = config === null || config === void 0 ? void 0 : config.loginForm.islandVertical;
    // Get logo from layout config
    const logo = config === null || config === void 0 ? void 0 : config.layout.logo;
    // Title and description from localization
    // No account display name configured -> generic welcome (avoids "Welcome to ")
    const title = (accountDisplayName === null || accountDisplayName === void 0 ? void 0 : accountDisplayName.trim())
        ? $L('uxp-core.auth.welcome-to', currentLanguage, { accountName: accountDisplayName })
        : $L('uxp-core.auth.welcome', currentLanguage);
    const description = $L('uxp-core.auth.sign-in-description', currentLanguage);
    /**
     * Render the form content (used in both island and fullpage modes)
     */
    const renderFormContent = () => (_jsxs(_Fragment, { children: [flashMessage && (_jsx("div", { className: "uxpcore_loginform__flash-message", children: flashMessage })), _jsxs("div", { className: "uxpcore_loginform__form-fields", children: [_jsx(FormField, { children: _jsx(Input, { type: "text", value: loginID, placeholder: $L('uxp-core.auth.user-id', currentLanguage), onChange: handleLoginIDChange, onKeyDown: handleKeyDown, prefix: _jsx(Icon, { icon: ['far', 'user'] }), fullWidth: true, isValid: loginIDError ? false : undefined }) }), _jsx(FormField, { children: _jsx(PasswordInput, { value: password, placeholder: $L('uxp-core.auth.password', currentLanguage), onChange: handlePasswordChange, onKeyDown: handleKeyDown, prefix: _jsx(Icon, { icon: ['far', 'lock'] }), fullWidth: true, isValid: passwordError ? false : undefined }) }), managePasswords && !disablePasswordMemory && (_jsx(FormField, { children: _jsx(Checkbox, { checked: keepLoggedIn, onChange: (val) => setKeepLoggedIn(val), label: $L('uxp-core.auth.keep-logged-in', currentLanguage) }) })), error && (_jsx("div", { className: "uxpcore_loginform__error-message", children: error }))] }), _jsxs("div", { className: "uxpcore_loginform__actions", children: [_jsx(ButtonComponent, { title: $L('uxp-core.auth.log-in', currentLanguage), onClick: handleLogin, disabled: !loginID || !password, className: "uxpcore_loginform__login-btn" }), allowPasswordReset && managePasswords && (_jsx("div", { className: "uxpcore_loginform__footer", children: _jsx("a", { className: "uxpcore_loginform__forgot-link", onClick: handleForgotPassword, children: $L('uxp-core.auth.forgot-password', currentLanguage) }) }))] }), _jsx(SSOButtons, { ssoConfig: ssoConfig, nextUrl: nextUrl, accountUrl: accountUrl, currentLanguage: currentLanguage })] }));
    /**
     * Render island horizontal mode (single column: branding top, form below)
     */
    const renderIslandHorizontal = () => {
        var _a;
        if (!islandHorizontalCfg)
            return null;
        return (_jsx("div", { className: classNames('uxpcore_loginform__island-wrapper', {
                [`uxpcore_loginform__island-wrapper--${islandHorizontalCfg.position}`]: true
            }), children: _jsxs("div", { className: classNames('uxpcore_loginform__island', 'uxpcore_loginform__island--horizontal', {
                    'uxpcore_loginform__island--glass': islandHorizontalCfg.glassEffect
                }), children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_loginform__language-selector-wrapper", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), logo && logo.url && (_jsx("div", { className: "uxpcore_loginform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_loginform__logo" }) })), title && (_jsx("h1", { className: "uxpcore_loginform__title", children: title })), description && (_jsx("p", { className: "uxpcore_loginform__description", children: description })), renderFormContent()] }) }));
    };
    /**
     * Render island vertical mode (two columns: branding left, form right)
     */
    const renderIslandVertical = () => {
        var _a;
        if (!islandVerticalCfg)
            return null;
        return (_jsx("div", { className: classNames('uxpcore_loginform__island-wrapper', {
                [`uxpcore_loginform__island-wrapper--${islandVerticalCfg.position}`]: true
            }), children: _jsxs("div", { className: classNames('uxpcore_loginform__island', 'uxpcore_loginform__island--vertical', {
                    'uxpcore_loginform__island--glass': islandVerticalCfg.glassEffect
                }), children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_loginform__language-selector-wrapper uxpcore_loginform__language-selector-wrapper--absolute", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), _jsxs("div", { className: "uxpcore_loginform__island-left", children: [logo && logo.url && (_jsx("div", { className: "uxpcore_loginform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_loginform__logo" }) })), title && (_jsx("h1", { className: "uxpcore_loginform__title", children: title })), description && (_jsx("p", { className: "uxpcore_loginform__description", children: description }))] }), _jsx("div", { className: "uxpcore_loginform__island-right", children: renderFormContent() })] }) }));
    };
    /**
     * Render fullpage mode (two-column layout)
     */
    const renderFullpage = () => {
        var _a;
        return (_jsxs("div", { className: "uxpcore_loginform__fullpage", children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_loginform__language-selector-wrapper uxpcore_loginform__language-selector-wrapper--absolute", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), _jsxs("div", { className: "uxpcore_loginform__fullpage-left", children: [logo && logo.url && (_jsx("div", { className: "uxpcore_loginform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_loginform__logo" }) })), title && (_jsx("h1", { className: "uxpcore_loginform__title", children: title })), description && (_jsx("p", { className: "uxpcore_loginform__description", children: description }))] }), _jsx("div", { className: "uxpcore_loginform__fullpage-right", children: _jsx("div", { className: "uxpcore_loginform__form-container", children: renderFormContent() }) })] }));
    };
    return (_jsxs("div", { className: "uxpcore_loginform__container", children: [isIslandHorizontal && renderIslandHorizontal(), isIslandVertical && renderIslandVertical(), isFullpage && renderFullpage(), _jsx(ForgotPassword, { show: showForgotPassword, onClose: () => setShowForgotPassword(false), accountUrl: accountUrl, captchaEnabled: captchaEnabled, captchaSiteKey: captchaSiteKey, currentLanguage: currentLanguage })] }));
};
export default LoginForm;
